import React from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { OverviewMetricsDailyPoint } from '../../../types/overviewMetrics';
import { formatCurrencyExact, formatImpressions } from './metricsFormat';

interface MetricsChartProps {
  data: OverviewMetricsDailyPoint[];
  loading?: boolean;
}

const formatDate = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Y-axis ticks: whole-dollar for large ranges is fine (axis labels are just
// scale guides; the tooltip carries the exact figures).
const formatDollarAxis = (dollars: number) =>
  `$${dollars.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const MetricsChart: React.FC<MetricsChartProps> = ({ data, loading }) => {
  if (loading) {
    return <div className="h-[280px] bg-slate-100 rounded-xl animate-pulse" />;
  }
  const chartData = data.map((p) => ({
    ...p,
    revenueDollars: p.grossCents / 100,
  }));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div style={{ width: '100%', height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 12, fill: '#64748b' }}
              stroke="#94a3b8"
            />
            <YAxis
              yAxisId="left"
              tickFormatter={formatDollarAxis}
              tick={{ fontSize: 12, fill: '#64748b' }}
              stroke="#94a3b8"
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={(v: number) => v.toLocaleString('en-US')}
              tick={{ fontSize: 12, fill: '#64748b' }}
              stroke="#94a3b8"
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                if (name === 'Revenue') return [formatCurrencyExact(value * 100), name];
                return [formatImpressions(value), name];
              }}
              labelFormatter={(label: string) => formatDate(label)}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="left" dataKey="revenueDollars" name="Revenue" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            <Line yAxisId="right" type="monotone" dataKey="deliveredImpressions" name="Impressions" stroke="#10b981" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MetricsChart;
